import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { companiesApi } from 'services/apiDigitalTicket/companies';
import { reportsApi } from 'services/apiDigitalTicket/reports';
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { myFirebase } from 'services/firebase';

// Helpers
import { redirectToModule } from 'helpers/redirectToModule';
import { setToken, setId, setClaims } from 'helpers/auth';
import { setDynamicErrorMessagersList } from 'helpers/errorMessagers';
// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { loginSuccess } from 'store/ducks/users/actions';

// Store
import { persistor } from 'store';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import ENVS from 'constants/enviroments';

import * as CompaniesActions from './actions';
import { CompaniesTypes, CompanyReportInterface } from './types';

function* list({
  payload,
}: ReturnType<typeof CompaniesActions.listCompaniesRequest>): any {
  try {
    const { body, filtering, loading = true, isCsvFull } = payload;
    if (loading && !filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando empresas' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.listCompaniesSuccess({
          companiesList: response.data,
          companiesListFull: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
          isCsvFull,
        }),
      );
      if (loading && !filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - listCompanies - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.listCompaniesFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* listBoardingHistory({
  payload,
}: ReturnType<typeof CompaniesActions.listBoardingHistoryRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando empresas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(reportsApi.reportList, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.listBoardingHistorySuccess({
          boardingHistoryList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - listBoardingHistory - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.listBoardingHistoryFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof CompaniesActions.getCompanyRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.getCompanySuccess({ companyData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - getCompany - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.getCompanyFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* auth({
  payload,
}: ReturnType<typeof CompaniesActions.authCompanyRequest>): any {
  try {
    // Clear all redux-persist
    persistor.flush();

    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde...' }));
    const response = yield call(companiesApi.companyAuth, {
      data: { ...payload },
    });

    if (response.status === 200) {
      const auth = getAuth(myFirebase);
      let firebaseLoginError = false;

      const fresponse = yield call(() =>
        signInWithEmailAndPassword(
          auth,
          response.data.email,
          payload.password,
        ).catch(() => {
          firebaseLoginError = true;
        }),
      );

      if (firebaseLoginError) {
        yield put(DialogsActions.setClose());
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'error',
            message: 'Por favor, confira email e senha',
          }),
        );
        return;
      }

      const token = yield call(() => auth.currentUser?.getIdToken(true));
      const userId = auth.currentUser?.uid;

      const tokenResult = yield call(() =>
        auth.currentUser?.getIdTokenResult(),
      );
      const userTypes = tokenResult?.claims.types;

      setId(userId);
      setToken(token);
      setClaims(tokenResult?.claims);

      const {
        displayName,
        email,
        emailVerified,
        isAnonymous,
        metadata,
        phoneNumber,
        photoURL,
        providerData,
        providerId,
        refreshToken,
        tenantId,
        uid,
      } = fresponse.user;

      yield put(
        loginSuccess({
          displayName,
          email,
          emailVerified,
          isAnonymous,
          metadata,
          phoneNumber,
          photoURL,
          providerData,
          providerId,
          refreshToken,
          tenantId,
          uid,
        }),
      );
      setDynamicErrorMessagersList();
      yield put(
        CompaniesActions.authCompanySuccess({ companyAuth: response.data }),
      );

      if (token && userTypes) {
        yield put(DialogsActions.setClose());
        if (Array.isArray(userTypes)) {
          if (userTypes.length > 1) {
            // Path for the SelectModule component
            yield put(push('/selecione-um-modulo'));
          } else {
            redirectToModule(userTypes[0].type, userTypes[0].CompanyID);
          }
        }
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('AuthCompany - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.authCompanyFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* report({
  payload,
}: ReturnType<typeof CompaniesActions.reportCompanyRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.report, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.reportCompanySuccess({
          companyReportList: response.data.map(
            (report: CompanyReportInterface) => ({
              ...report,
              paymentMethod: report?.PaymentRequest?.type || '',
            }),
          ),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Company - report - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.reportCompanyFailure());
    yield put(DialogsActions.setClose());
  }
}

function* forgotCompanyPassword({
  payload,
}: ReturnType<typeof CompaniesActions.forgotCompanyRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading());

    const response = yield call(companiesApi.getAuthCompanyEmail, {
      data: {
        ...payload,
        domain: ENVS.PASSWORD_REDIRECT
          ? `${ENVS.PASSWORD_REDIRECT}/empresas`
          : 'https://bilhetagemdigital.com.br',
      },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'info',
          message: 'Foi enviado para o e-mail um link para alterar a senha',
        }),
      );
    }

    yield put(CompaniesActions.forgotCompanySuccess());
    yield put(DialogsActions.setClose());
    if (!payload.id) {
      yield put(push('/empresas'));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - forgotPassword - error', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CompaniesActions.forgotCompanyFailure());
  }
}

function* forgotPassword({
  payload,
}: ReturnType<typeof CompaniesActions.forgotPasswordRequest>): any {
  try {
    const auth = getAuth(myFirebase);

    yield put(DialogsActions.setOpenLoading());

    const response = yield call(companiesApi.getAuthEmail, {
      params: { ...payload },
    });

    yield call(() =>
      sendPasswordResetEmail(auth, response.data.email, {
        url:
          `${ENVS.PASSWORD_REDIRECT}/empresas` ||
          'https://bilhetagemdigital.com.br',
      }),
    );

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'info',
        message: payload.internal
          ? 'Foi enviado para o e-mail um link para alterar a senha'
          : 'Foi enviado para você um e-mail com link para alterar sua senha',
      }),
    );
    yield put(CompaniesActions.forgotPasswordSuccess());
    yield put(DialogsActions.setClose());
    if (!payload.internal) yield put(push('/'));
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - forgotPassword - error', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(CompaniesActions.forgotPasswordFailure());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof CompaniesActions.filterCompaniesRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.filterCompaniesSuccess({
          companiesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      if (!payload?.filtering) {
        yield put(DialogsActions.setClose());
      }

      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - filterCompanies - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.filterCompaniesFaliure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof CompaniesActions.createCompanyRequest>): any {
  try {
    const { body, redirectTo } = payload;

    yield put(DialogsActions.setOpenLoading({ title: 'Cadastrando empresa' }));

    const response = yield call(companiesApi.create, { data: { ...body } });

    if (response.status === 200) {
      yield call(companiesApi.getAuthCompanyEmail, {
        data: {
          id: response.data.id,
          domain:
            `${ENVS.PASSWORD_REDIRECT}/empresas` ||
            'https://bilhetagemdigital.com.br',
        },
      });

      yield put(
        CompaniesActions.createCompanySuccess({
          companyCreated: response.data,
        }),
      );
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message:
            'Empresa cadastrada com sucesso. E-mail de definição de senha enviado.',
        }),
      );

      if (redirectTo) yield put(push(redirectTo));

      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('companies - createCompany - error', error);

    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );

    yield put(CompaniesActions.createCompanyFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.resolve(error);
  }
}

function* update({
  payload,
}: ReturnType<typeof CompaniesActions.updateCompanyRequest>): any {
  try {
    const { body, redirectTo } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.update, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.updateCompanySuccess({ companyData: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Empresa alterada com sucesso',
        }),
      );
      if (redirectTo) yield put(push(redirectTo));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - updateCompany - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.updateCompanyFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateReportLimit({
  payload,
}: ReturnType<typeof CompaniesActions.updateReportLimitRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.updateReportLimit, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.updateReportLimitSuccess({
          companyData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Consultas alteradas com sucesso',
        }),
      );

      yield put(
        CompaniesActions.listCompaniesRequest({
          body: {
            _includes: 'reportlimit',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - updateReportLimit - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.updateReportLimitFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof CompaniesActions.deleteCompanyRequest> | any): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo empresa' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const { isDeleteList, body } = payload;
    const response = yield call(companiesApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.deleteCompanySuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Empresa excluída com sucesso',
        }),
      );
      if (isDeleteList) yield put(CompaniesActions.listCompaniesRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - deleteCompany - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.deleteCompanyFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listPaymentMethod({
  payload,
}: ReturnType<typeof CompaniesActions.listPaymentMethodRequest>): any {
  const normalizeChannels = (channels: unknown) => {
    if (Array.isArray(channels)) {
      return channels;
    }

    if (typeof channels !== 'string' || !channels.trim()) {
      return [];
    }

    try {
      const parsedChannels = JSON.parse(channels);
      return Array.isArray(parsedChannels) ? parsedChannels : [];
    } catch (_error) {
      return [];
    }
  };

  try {
    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando Metodos de pagamento',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.paymentMethodList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.listPaymentMethodSuccess({
          paymentMethodList: response.data.map((item: any) => ({
            ...item,
            channels: normalizeChannels(item?.channels),
          })),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - listPaymentMethod - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.listPaymentMethodFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* createPaymentMethod({
  payload,
}: ReturnType<typeof CompaniesActions.createPaymentMethodRequest>): any {
  try {
    const { body } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Cadastrando Método de pagamento',
      }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.paymentMethodCreate, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(DialogsActions.setClose());

      yield put(
        CompaniesActions.createPaymentMethodSuccess({
          paymentMethodData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Método de pagamento cadastrado com sucesso',
        }),
      );
      yield put(CompaniesActions.listPaymentMethodRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('companies - createPaymentMethod - error', error);

    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );

    yield put(CompaniesActions.createPaymentMethodFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updatePaymentMethod({
  payload,
}: ReturnType<typeof CompaniesActions.updatePaymentMethodRequest>): any {
  try {
    const { body } = payload;
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando Método de pagamento' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.paymentMethodUpdate, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.updatePaymentMethodSuccess({
          paymentMethodData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Método de pagamento com sucesso',
        }),
      );

      yield put(CompaniesActions.listPaymentMethodRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - updatePaymentMethod - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.updatePaymentMethodFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updatePaymentMethodStatus({
  payload,
}: ReturnType<typeof CompaniesActions.updatePaymentMethodStatusRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando Método de pagamento' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.paymentMethodStatusUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.updatePaymentMethodStatusSuccess({
          paymentMethodData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Método de pagamento com sucesso',
        }),
      );

      yield put(CompaniesActions.listPaymentMethodRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - updatePaymentMethodStatus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.updatePaymentMethodStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deletePaymentMethod({
  payload,
}: ReturnType<typeof CompaniesActions.deletePaymentMethodRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo Método de pagamento' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.paymentMethodDelete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Método de pagamento excluído com sucesso',
        }),
      );
      yield put(CompaniesActions.listPaymentMethodRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - deletePaymentMethod - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.deletePaymentMethodFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createVehicle({
  payload,
  meta,
}: ReturnType<typeof CompaniesActions.createVehicleRequest>): any {
  try {
    const { body, redirectTo } = payload;

    yield put(DialogsActions.setOpenLoading({ title: 'Cadastrando Veículo' }));

    const response = yield call(companiesApi.vehicleCreate, {
      data: { ...body },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.createVehicleSuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Veículo cadastrada com sucesso.',
        }),
      );
      window.location.reload();
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('companies - createVehicle - error', error);

    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );

    yield put(CompaniesActions.createVehicleFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.resolve(error);
  }
}

function* updateVehicle({
  payload,
}: ReturnType<typeof CompaniesActions.updateVehicleRequest>): any {
  try {
    const { body } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.vehicleUpdate, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.updateVehicleSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Veículo alterada com sucesso',
        }),
      );
      window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - updateVehicle - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.updateVehicleFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deleteVehicle({
  payload,
}: ReturnType<typeof CompaniesActions.deleteVehicleRequest> | any): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo veículo' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.vehicleDelete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.deleteVehicleSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Veículo excluída com sucesso',
        }),
      );
      window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - deleteVehicle - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.deleteVehicleFailure());
    yield put(DialogsActions.setClose());
  }
}

function* companyBoletoConfigList({
  payload,
}: ReturnType<typeof CompaniesActions.companyBoletoConfigListRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando empresas' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(companiesApi.companyBoletoConfigList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        CompaniesActions.companyBoletoConfigListSuccess({
          companyBoletoConfigList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('companies - companyBoletoConfigList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );
    yield put(CompaniesActions.companyBoletoConfigListFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* companyBoletoConfigCreateMany({
  payload,
}: ReturnType<
  typeof CompaniesActions.companyBoletoConfigCreateManyRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando data de boleto' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(companiesApi.companyBoletoConfigCreateMany, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.companyBoletoConfigCreateManySuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Data de boleto cadastrada com sucesso',
        }),
      );
      yield put(
        CompaniesActions.companyBoletoConfigListRequest({ _limit: 9999 }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('companies - companyBoletoConfigCreateMany - error', error);

    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );

    yield put(CompaniesActions.companyBoletoConfigCreateManyFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* companyBoletoConfigUpdateMany({
  payload,
}: ReturnType<
  typeof CompaniesActions.companyBoletoConfigUpdateManyRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando data de boleto' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(companiesApi.companyBoletoConfigUpdateMany, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.companyBoletoConfigUpdateManySuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Data de boleto atualizada com sucesso',
        }),
      );
      yield put(
        CompaniesActions.companyBoletoConfigListRequest({ _limit: 9999 }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('companies - companyBoletoConfigUpdateMany - error', error);

    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );

    yield put(CompaniesActions.companyBoletoConfigUpdateManyFaliure());
    yield put(DialogsActions.setClose());
  }
}

function* companyBoletoConfigDelete({
  payload,
}: ReturnType<typeof CompaniesActions.companyBoletoConfigDeleteRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Apagando data de boleto' }),
    );

    const response = yield call(companiesApi.companyBoletoConfigDelete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(CompaniesActions.companyBoletoConfigDeleteSuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Data de boleto apagado com sucesso',
        }),
      );
      yield put(
        CompaniesActions.companyBoletoConfigListRequest({ _limit: 9999 }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);
    console.error('companies - companyBoletoConfigDelete - error', error);

    yield put(
      SnackbarsActions.setOpen({
        message,
        open: true,
        type: 'error',
      }),
    );

    yield put(CompaniesActions.companyBoletoConfigDeleteFaliure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const companiesSagas = [
  takeLatest(CompaniesTypes.LIST_COMPANIES_REQUEST, list),
  takeLatest(CompaniesTypes.LIST_BOARDING_HISTORY_REQUEST, listBoardingHistory),
  takeLatest(CompaniesTypes.GET_COMPANY_REQUEST, get),
  takeLatest(CompaniesTypes.AUTH_COMPANIES_REQUEST, auth),
  takeLatest(CompaniesTypes.REPORT_COMPANY_REQUEST, report),
  takeLatest(CompaniesTypes.FORGOT_PASSWORD_REQUEST, forgotPassword),
  takeLatest(CompaniesTypes.FILTER_COMPANIES_REQUEST, filter),
  takeLatest(CompaniesTypes.CREATE_COMPANY_REQUEST, create),
  takeLatest(CompaniesTypes.UPDATE_COMPANY_REQUEST, update),
  takeLatest(CompaniesTypes.UPDATE_REPORT_LIMIT_REQUEST, updateReportLimit),
  takeLatest(CompaniesTypes.DELETE_COMPANY_REQUEST, remove),
  takeLatest(CompaniesTypes.FORGOT_COMPANIES_REQUEST, forgotCompanyPassword),
  takeLatest(CompaniesTypes.LIST_MANAGEMENT_PAYMENT_REQUEST, listPaymentMethod),
  takeLatest(
    CompaniesTypes.CREATE_MANAGEMENT_PAYMENT_REQUEST,
    createPaymentMethod,
  ),
  takeLatest(
    CompaniesTypes.UPDATE_MANAGEMENT_PAYMENT_REQUEST,
    updatePaymentMethod,
  ),
  takeLatest(
    CompaniesTypes.UPDATE_MANAGEMENT_PAYMENT_STATUS_REQUEST,
    updatePaymentMethodStatus,
  ),
  takeLatest(
    CompaniesTypes.DELETE_MANAGEMENT_PAYMENT_REQUEST,
    deletePaymentMethod,
  ),
  takeLatest(CompaniesTypes.CREATE_VEHICLE_REQUEST, createVehicle),
  takeLatest(CompaniesTypes.UPDATE_VEHICLE_REQUEST, updateVehicle),
  takeLatest(CompaniesTypes.DELETE_VEHICLE_REQUEST, deleteVehicle),
  takeLatest(
    CompaniesTypes.COMPANY_BOLETO_CONFIG_CREATE_MANY_REQUEST,
    companyBoletoConfigCreateMany,
  ),
  takeLatest(
    CompaniesTypes.COMPANY_BOLETO_CONFIG_LIST_REQUEST,
    companyBoletoConfigList,
  ),
  takeLatest(
    CompaniesTypes.COMPANY_BOLETO_CONFIG_UPDATE_MANY_REQUEST,
    companyBoletoConfigUpdateMany,
  ),
  takeLatest(
    CompaniesTypes.COMPANY_BOLETO_CONFIG_DELETE_REQUEST,
    companyBoletoConfigDelete,
  ),
];
